import React, { useState } from 'react';


// Template notes shown above the upload box
export const BulkImportUDIsIntro = () => (
    <div style={{ fontSize: "11px", textAlign: "left" }}>
        <p>Use this option to automatically upload UDIs using our MedBoard template for bulk upload. If you wish to use this option, please contact us and we will guide you to fill the form and evaluate your needs/data.</p>

        <p> Current EU UDI Upload Template version: v8</p>
        <p> Current US UDI Upload Template version: v5</p>
    </div>
);

// Endpoints answer with a plain or JSON-encoded string
const readResponseMessage = async (response) => {
    const text = await response.text();
    try {
        const parsed = JSON.parse(text);
        return typeof parsed === "string" ? parsed : parsed?.message;
    } catch (e) {
        return text;
    }
};

const BulkImportUDIs = ({ defaultUdiType = "", onUploaded = null }) => {
    const [file, setFile] = useState(null);
    const [udiType, setUdiType] = useState(defaultUdiType); // "US" | "EU"
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [isDragging, setIsDragging] = useState(false); // Track drag state

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setFile(e.target.files[0]);
            setMessage(null);
        }
    };

    // --- Drag and Drop Handlers ---
    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            setFile(e.dataTransfer.files[0]);
            setMessage(null);
            e.dataTransfer.clearData();
        }
    };
    // ------------------------------

    const handleUpload = async () => {
        if (!udiType) {
            setMessage({ type: "error", text: "Please select UDI type (US or EU)." });
            return;
        }

        if (!file) {
            setMessage({ type: "error", text: "Please select a file first." });
            return;
        }

        let url = "";

        if (udiType === "US") url = "/umbraco/api/RIMSProducts/BulkImportUSUDI";
        if (udiType === "EU") url = "/umbraco/api/RIMSProducts/BulkImportEUUDI";

        setLoading(true);
        setMessage(null);

        const formData = new FormData();
        formData.append("file", file);
        formData.append("udiType", udiType); // send selection to backend

        try {
            const response = await fetch(url, {
                method: "POST",
                body: formData,
            });

            if (!response.ok) {
                const errorText = await readResponseMessage(response);
                throw new Error(errorText || "Upload failed");
            }

            const successText = await readResponseMessage(response);

            setMessage({
                type: "success",
                text: successText || "File uploaded successfully.",
            });

            // optional reset
            setFile(null);
            setUdiType(defaultUdiType);

            if (onUploaded) onUploaded();
        } catch (err) {
            setMessage({
                type: "error",
                text: err.message || "Something went wrong.",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            style={{
                maxWidth: "400px",
                margin: "0 auto",
                padding: "24px",
                border: "1px solid #e0e0e0",
                borderRadius: "12px",
                background: "#fff",
                display: "flex",
                flexDirection: "column",
                gap: "20px",
                boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)"
            }}
        >
            {/* UDI Type Selection */}
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <label style={{ fontSize: "0.85rem", fontWeight: "500", color: "#4b5563" }}>
                    UDI Type
                </label>
                <select
                    value={udiType}
                    onChange={(e) => {
                        setUdiType(e.target.value);
                        setMessage(null);
                    }}
                    style={{
                        width: "100%",
                        padding: "10px",
                        borderRadius: "8px",
                        border: "1px solid #d1d5db",
                        backgroundColor: "#fff",
                        outline: "none"
                    }}
                >
                    <option value="">Select UDI Type</option>
                    <option value="US">US UDIs</option>
                    <option value="EU">EU UDIs</option>
                </select>
            </div>

            {/* Modern File Input Dropzone */}
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <label style={{ fontSize: "0.85rem", fontWeight: "500", color: "#4b5563" }}>
                    Upload template
                </label>
                <label
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "30px 20px",
                        border: "2px dashed",
                        borderColor: isDragging ? "#3498db" : "#d1d5db",
                        borderRadius: "8px",
                        cursor: "pointer",
                        backgroundColor: isDragging ? "#ebf5fb" : "#f9fafb",
                        transition: "all 0.2s ease"
                    }}
                    onMouseOver={(e) => { if (!isDragging) e.currentTarget.style.borderColor = "#3498db"; }}
                    onMouseOut={(e) => { if (!isDragging) e.currentTarget.style.borderColor = "#d1d5db"; }}
                >
                    <i className="fas fa-upload" style={{ fontSize: "24px", color: isDragging ? "#3498db" : "#9ca3af", marginBottom: "8px" }}></i>
                    <span style={{ textAlign: "center", fontSize: "13px", color: "#6b7280" }}>
                        {file ? file.name : "Click to select a file or drag and drop"}
                    </span>
                    <input
                        type="file"
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                    />
                </label>
            </div>

            {/* Primary Action Button */}
            <button
                onClick={handleUpload}
                disabled={loading || !file || !udiType}
                type="submit"
                className="btn u-btn-blue g-font-size-13 g-pl-12 g-pr-12 g-pt-8 g-pb-8"
                style={{
                    borderRadius: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "8px",
                    cursor: loading || !file || !udiType ? "not-allowed" : "pointer",
                    opacity: loading || !file || !udiType ? 0.6 : 1,
                    border: "none",
                    width: "100%",
                    transition: "background 0.2s",
                    padding: "10px"
                }}
            >
                <i className={loading ? "fas fa-spinner fa-spin" : "fas fa-arrow-up"}></i>
                {loading ? "Uploading..." : "Upload UDIs from Template"}
            </button>

            {/* Status Message */}
            {message && (
                <div
                    style={{
                        padding: "12px",
                        borderRadius: "8px",
                        background: message.type === "error" ? "#fff1f2" : "#f0fdf4",
                        color: message.type === "error" ? "#be123c" : "#15803d",
                        fontSize: "0.85rem",
                        whiteSpace: "pre-line",
                        border: `1px solid ${message.type === "error" ? "#fecdd3" : "#bbf7d0"}`
                    }}
                >
                    {message.text}
                </div>
            )}
        </div>
    );
};



export default BulkImportUDIs;
